import { motion } from "framer-motion";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { laminateFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { Link } from "react-router-dom";
import { History, Sparkles, ArrowUpRight, Layers } from "lucide-react";
import heroImg from "@/assets/waterproof-laminate-entryway-hero.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const WaterproofLaminateInstallation = () => {
  usePageSEO({
    title: "Laminate Flooring Installation | Quick-Step Certified",
    description: "Waterproof laminate flooring installation by Quick-Step Certified specialists since 1998. Uniclic technology, flush-mount stair nosings, NWFA standards.",
    path: "/laminate-installation",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Services" },
            { label: "Waterproof Laminate Installation" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt="Waterproof laminate flooring installation with flush stair nosings in Michigan MI 1 Wood Floors"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Waterproof Laminate Installation Michigan
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Waterproof Laminate Installation
            </h1>
            <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mt-2 leading-[1.2]">
              Precision Engineering & Perimeter Sealing Mastery
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              From our 1998 Pergo endorsement to today's Quick-Step Uniclic technology. Twenty-five years of laminate expertise, elevated with architectural finishes most installers have never heard of.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Request a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
              >
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ THE PERGO HERITAGE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">25 Year Legacy</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Pergo Heritage
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <History className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">Where It Started</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      In 1998, we became one of Michigan's first endorsed Pergo installers. Back then, laminate planks were glued together at the joints, a painstaking process that demanded precision and patience. We learned the category from its infancy, understanding the science of expansion gaps, perimeter sealing, and substrate preparation long before most contractors entered the market.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">Where It Is Now</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Today's{" "}
                      <Link to="/brands/quick-step" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                        Quick-Step NatureTEK waterproof collections
                      </Link>
                      {" "}are unrecognizable from those early glue-together planks. The patented Uniclic locking system clicks together without adhesive, and the HydroSeal coating protects every joint from moisture penetration. These floors offer superior wear, stain, and fade resistance compared to real wood, often backed by lifetime residential warranties.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ EXPONENTIAL IMPROVEMENT ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Performance Engineering</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                Exponential Improvement
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Modern waterproof laminate is no longer a compromise. It is a category that has surpassed traditional hardwood in several measurable dimensions. Scratch resistance rated at AC5 commercial grade. Complete waterproof protection for 24 hours or more. Fade resistance that maintains color integrity even in direct sunlight. And installation speed that cuts project timelines in half compared to nail-down hardwood.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  The key is specification. Not all laminate is created equal. We curate products with HDF cores rated above 900 kg/m³ density, wear layers with aluminum oxide reinforcement, and surface textures that replicate real wood grain through Registered Embossing technology. The result is a floor that looks like hardwood, outperforms hardwood in durability, and costs significantly less.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    Lifetime residential warranty on Quick-Step NatureTEK collections
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ THE SEAMLESS STAIRCASE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Staircase Integration</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Seamless Staircase
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Most laminate installers use overlap stair nosings. These are the cheap, rounded-lip pieces that stick out past the tread edge and scream "budget renovation." We eliminate them entirely. Our flush-mount stair nosings sit perfectly level with the tread surface, creating a clean, modern edge that looks like a custom hardwood staircase.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every nosing is color-matched to the laminate plank, mechanically fastened to the subfloor, and sealed at every joint. The result is a staircase that flows seamlessly from landing to landing, with no visible hardware, no overlapping lips, and no compromise. We treat laminate staircases with the same architectural precision we bring to our hardwood installations.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ ARCHITECTURAL ACCESSORIES ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Premium Details</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                Accessories That Elevate
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8"
              >
                <Layers className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-3">Ali Trims by Stairrods</h3>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Plastic T-moldings cheapen any floor. We replace them with precision-machined Ali Trims from Stairrods UK and Europe. Available in brushed chrome, antique brass, satin nickel, and matte black, these aluminum transition profiles transform every threshold into an architectural detail.
                </p>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8"
              >
                <Layers className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-3">TOP Step Flush Mount Vents</h3>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Our grain-matched flush-mount vents sit perfectly level with the laminate surface. No raised metal registers, no trip hazards, no visual interruption. Every vent is custom-ordered to match your specific plank color and texture, creating an invisible integration that defines our standard of care.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Laminate, elevated to <em className="italic font-normal text-[hsl(var(--gold-300))]">architecture.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                Twenty-five years of laminate mastery. From Pergo glue-together to Quick-Step Uniclic. We bring hardwood-level precision to every plank.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <QuoteModal
                trigger={
                  <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                    Schedule a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
              >
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        <RelatedServices
          heading="Every great laminate floor starts beneath the surface."
          subheading="Foundation & Product Selection"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "Uniclic technology is notoriously unforgiving to uneven surfaces. Laser-guided flatness mapping, self-leveling compounds, and mechanical grinding to meet our 1/8-inch mandate.", to: "/subfloor-leveling", tag: "Foundation" },
            { title: "Quick-Step NatureTEK Collections", desc: "Browse all Quick-Step waterproof laminate collections featuring SpillProtect24 technology, Uniclic locking, and FloorScore certified indoor air quality.", to: "/brands/quick-step", tag: "Product Selection" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />
        <PageFAQ suppressJsonLd faqs={laminateFAQs} heading={<>Laminate installation questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Laminate Installation FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default WaterproofLaminateInstallation;
